export interface HelpItem {
  label: string;
  description: string;
}

export interface HelpSection {
  title: string;
  items: readonly HelpItem[];
}

export interface ModuleHelpTopic {
  id: string;
  title: string;
  summary: string;
  search: string;
  sections: readonly HelpSection[];
}

function section(title: string, items: Record<string, string>): HelpSection {
  return {
    title,
    items: Object.entries(items).map(([label, description]) => ({ label, description }))
  };
}

const topics: readonly ModuleHelpTopic[] = [
  {
    id: 'dashboard',
    title: 'Panoramica: come leggerla',
    summary:
      'È il cruscotto del workspace: riunisce indicatori e priorità, ma i dati si correggono sempre nel modulo da cui provengono.',
    search: 'panoramica dashboard indicatori widget attività recenti',
    sections: [
      section('Termini e indicatori', {
        KPI: 'Un numero sintetico che misura un aspetto del lavoro, per esempio ore, progetti o spese.',
        'Attività recente':
          'Una modifica registrata nel workspace. Non è un’attività da svolgere del modulo Attività.',
        Widget: 'Un riquadro del cruscotto che mostra un indicatore o un elenco operativo.'
      }),
      section('Comandi', {
        'Cambia periodo':
          'Ricalcola gli indicatori nell’intervallo scelto senza modificare i record.',
        'Riordina widget':
          'Cambia la posizione dei riquadri solo per rendere più utile la lettura.',
        Aggiorna: 'Ricarica i dati più recenti dal server.'
      }),
      section('Come interpretarla', {
        'Valore vuoto': 'Significa che nel periodo non ci sono dati disponibili o accessibili.',
        'Apri dettaglio': 'Porta al modulo sorgente, dove puoi verificare e modificare il record.'
      })
    ]
  },
  {
    id: 'clients',
    title: 'Clienti: anagrafica e collegamenti',
    summary:
      'Raccoglie aziende, persone e prospect. Lo stesso cliente può essere collegato a CRM, progetti, tempo, scadenze e spese.',
    search: 'clienti anagrafica prospect stato archivia collegamenti',
    sections: [
      section('Termini', {
        Prospect: 'Contatto potenziale non ancora acquisito.',
        Cliente: 'Persona o organizzazione con cui esiste una relazione operativa o commerciale.',
        Referente: 'Persona principale da contattare presso l’organizzazione.',
        Tag: 'Etichetta libera usata per classificare e ritrovare i clienti.'
      }),
      section('Azioni', {
        'Nuovo cliente': 'Apre la scheda anagrafica. Il nome è il dato minimo necessario.',
        Modifica: 'Aggiorna l’anagrafica; i collegamenti esistenti restano validi.',
        Archivia: 'Nasconde il cliente dalle viste operative senza cancellarne la cronologia.',
        Importa: 'Carica più anagrafiche da un file supportato.',
        Esporta: 'Scarica i record che hai il permesso di leggere.'
      }),
      section('Filtri e risultati', {
        Cerca: 'Filtra per dati identificativi visibili, senza modificare i record.',
        Stato: 'Restringe l’elenco in base alla fase corrente del rapporto.',
        Duplicati: 'Segnala possibili corrispondenze; devi verificare prima di salvare.'
      })
    ]
  },
  {
    id: 'crm',
    title: 'CRM: dalla proposta al progetto',
    summary:
      'Segue una possibile vendita lungo la pipeline. Un’opportunità vinta con cliente collegato può essere trasformata in progetto.',
    search: 'CRM opportunità pipeline fasi probabilità vinta persa crea progetto',
    sections: [
      section('Termini', {
        Opportunità: 'Una possibile vendita o collaborazione ancora da qualificare.',
        Pipeline: 'La sequenza di fasi attraversata dall’opportunità.',
        Probabilità: 'Stima commerciale, non garanzia, della possibilità di chiusura.',
        'Valore previsto': 'Importo potenziale dell’opportunità nella valuta indicata.',
        'Prossima azione': 'Passo concreto da compiere per far avanzare la trattativa.'
      }),
      section('Fasi', {
        'Nuova (fase 0)':
          'Idea o contatto appena registrato: non è ancora stato qualificato né trasformato in una proposta.',
        Qualificazione:
          'Si verificano bisogno, interlocutore, fattibilità e valore prima di preparare una proposta.',
        Proposta: 'La soluzione commerciale è in preparazione e non risulta ancora inviata.',
        Inviata: 'Offerta o proposta condivisa con il potenziale cliente.',
        Negoziazione: 'Condizioni, prezzo o perimetro sono in discussione.',
        Vinta: 'La trattativa è conclusa positivamente e può generare un progetto.',
        Persa: 'La trattativa è chiusa senza accordo; indica il motivo per analizzarla.'
      }),
      section('Azioni', {
        'Sposta fase': 'Aggiorna la fase e la probabilità coerente proposta dal sistema.',
        'Segna vinta/persa': 'Chiude la trattativa; Persa richiede una motivazione.',
        'Crea progetto': 'Copia i dati utili in un nuovo progetto, senza eliminare l’opportunità.',
        Modifica: 'Aggiorna valore, date, cliente e prossima azione.'
      })
    ]
  },
  {
    id: 'projects',
    title: 'Progetti: commesse, attività e persone',
    summary:
      'Un progetto organizza un risultato da consegnare. Può contenere attività, milestone, membri, budget e collegamenti al cliente.',
    search: 'progetto attività milestone membri budget stato priorità',
    sections: [
      section('Termini', {
        Progetto: 'Contenitore operativo per una commessa, iniziativa o risultato.',
        Milestone: 'Traguardo significativo con una scadenza, non un’attività eseguibile.',
        Membro: 'Utente che partecipa al progetto con un ruolo descritto.',
        Budget: 'Limite o previsione economica; non produce automaticamente fatture.',
        Repository: 'Indirizzo del repository di codice o documenti tecnici.'
      }),
      section('Collegamenti', {
        Attività: 'Divide il progetto in lavori concreti, assegnabili e tracciabili.',
        Tempo: 'Le registrazioni collegate alimentano il consuntivo del progetto.',
        Spese: 'I costi collegati restano consultabili anche nel modulo Spese.',
        Cliente: 'Indica per chi viene svolto il progetto.'
      }),
      section('Azioni e stati', {
        Dettagli: 'Apre milestone, membri e informazioni complete.',
        'Apri attività': 'Mostra Attività già filtrato sul progetto.',
        Modifica: 'Aggiorna la scheda senza ricreare il progetto.',
        Archivia: 'Rimuove il progetto dalle viste operative mantenendo la storia.'
      })
    ]
  },
  {
    id: 'notes',
    title: 'Note: documenti, cartelle e collaborazione',
    summary:
      'Le note sono documenti condivisi in tempo reale, organizzabili in cartelle. Le cartelle Note non sono workspace ERP.',
    search: 'note cartelle editor collaborazione cronologia commenti condivisione',
    sections: [
      section('Termini', {
        Cartella: 'Raggruppa note per argomento. Non cambia utenti, permessi o dati del workspace.',
        'Workspace ERP':
          'Ambiente separato con propri membri e record; si crea nell’area amministrativa.',
        Cronologia: 'Versioni precedenti della nota che puoi consultare o ripristinare.',
        Commento: 'Discussione associata alla nota, distinta dal contenuto del documento.',
        Presenza: 'Indica chi sta visualizzando o modificando la nota in tempo reale.'
      }),
      section('Azioni', {
        Nuova: 'Crea una nota e permette di scegliere la cartella.',
        'Nuova cartella': 'Crea un contenitore nella barra laterale delle Note.',
        Rinomina: 'Cambia il nome della cartella senza spostare o cancellare le note.',
        'Elimina cartella':
          'Rimuove la cartella; la finestra di conferma spiega cosa accade alle note contenute.',
        Condividi: 'Copia o prepara il collegamento alla nota secondo i permessi del workspace.',
        'Modalità concentrazione':
          'Riduce gli elementi laterali per lasciare più spazio all’editor.',
        Cronologia: 'Mostra le versioni e consente un ripristino esplicito.'
      }),
      section('Limiti', {
        'Collegamento ai progetti':
          'Le note non diventano automaticamente attività e non ereditano un progetto.',
        'Salvataggio collaborativo':
          'Le modifiche sono sincronizzate; eventuali problemi di connessione sono segnalati nell’editor.'
      })
    ]
  },
  {
    id: 'tasks',
    title: 'Attività: il lavoro da eseguire',
    summary:
      'Un’attività è un’unità di lavoro concreta. Può appartenere a un progetto e può avviare un timer mantenendo quel collegamento.',
    search: 'attività progetto stato priorità stima timer assegnatario',
    sections: [
      section('Termini', {
        Attività: 'Lavoro concreto con titolo, stato, priorità e scadenza facoltativa.',
        Stato: 'Indica se il lavoro è da iniziare, in corso, bloccato o completato.',
        Priorità: 'Ordine relativo di urgenza; non modifica automaticamente la scadenza.',
        Stima: 'Tempo previsto, utile per confrontare previsione e consuntivo.',
        Etichette: 'Parole chiave per classificare e filtrare il lavoro.'
      }),
      section('Collegamenti', {
        Progetto: 'Facoltativo. Se presente, l’attività compare nel lavoro del progetto.',
        Timer: 'Avvia una misurazione del tempo collegata ad attività e progetto.',
        Registrazione: 'Fermando il timer viene salvata una voce nel modulo Tempo.'
      }),
      section('Azioni', {
        'Nuova attività': 'Crea il lavoro; scegli un progetto se fa parte di una commessa.',
        'Avvia timer': 'Inizia il conteggio senza completare o cambiare lo stato.',
        Modifica: 'Aggiorna tutti i valori del record.',
        Completa: 'Segna il lavoro terminato; non chiude automaticamente il progetto.'
      })
    ]
  },
  {
    id: 'time',
    title: 'Tempo: timer e consuntivi',
    summary:
      'Registra quanto tempo è stato dedicato al lavoro, usando il timer o inserendo manualmente un intervallo concluso.',
    search: 'tempo timer registrazione manuale fatturabile tariffa progetto attività',
    sections: [
      section('Termini', {
        Registrazione: 'Intervallo di lavoro con data, inizio, fine e contesto.',
        Fatturabile:
          'Indica che il tempo può essere valorizzato verso il cliente; non crea una fattura.',
        'Tariffa oraria': 'Valore economico di un’ora, usato per calcolare un importo indicativo.',
        Consuntivo: 'Tempo realmente registrato, distinto dalla stima di un’attività.'
      }),
      section('Azioni', {
        'Avvia timer': 'Inizia a misurare; puoi mettere in pausa, riprendere, fermare o scartare.',
        'Aggiungi manualmente': 'Registra un intervallo già terminato.',
        Pausa: 'Sospende il conteggio senza salvare ancora la registrazione.',
        Ferma: 'Conclude e salva il tempo accumulato.',
        Scarta: 'Elimina il timer corrente senza creare una registrazione.',
        Modifica: 'Corregge una registrazione già salvata.'
      }),
      section('Coerenza', {
        'Attività e progetto':
          'Se scegli un’attività collegata, il progetto coerente viene mantenuto o proposto.',
        Intervallo:
          'La fine deve essere successiva all’inizio; gli errori impediscono il salvataggio.'
      })
    ]
  },
  {
    id: 'billing',
    title: 'Scadenze: entrate, costi e rinnovi',
    summary:
      'Pianifica movimenti e ricorrenze. Non sostituisce la contabilità fiscale né emette documenti fiscali.',
    search: 'scadenze billing entrata costo ricorrenza rinnovo tolleranza',
    sections: [
      section('Termini', {
        Entrata: 'Somma prevista in ingresso.',
        Costo: 'Somma prevista in uscita.',
        Ricorrenza: 'Frequenza con cui la scadenza si ripete.',
        Rinnovo: 'Modalità con cui la voce prosegue alla scadenza.',
        'Giorni di tolleranza': 'Margine dopo la data prevista prima di considerarla in ritardo.',
        'Termine operativo':
          'Data di scadenza più giorni di tolleranza. Dal giorno successivo un residuo aperto risulta scaduto.'
      }),
      section('Azioni', {
        'Nuova scadenza': 'Crea una voce singola o ricorrente.',
        'Pagamenti e saldo':
          'Registra incassi, pagamenti effettivi e rettifiche senza cancellare lo storico.',
        'Posticipa scadenza':
          'Sposta una scadenza aperta a una data successiva e conserva data precedente e motivazione.',
        Sospendi: 'Interrompe temporaneamente le future elaborazioni della voce.',
        Riprendi: 'Riattiva una voce sospesa.',
        Archivia: 'La rimuove dall’operatività conservandone la storia.',
        Modifica: 'Aggiorna importo, soggetto, progetto e calendario.'
      }),
      section('Limiti', {
        'Non è una fattura': 'La voce pianifica un movimento ma non crea un documento fiscale.',
        'Progetto collegato':
          'Serve per attribuzione e ricerca; non cambia il budget automaticamente.'
      })
    ]
  },
  {
    id: 'analytics',
    title: 'Analytics: incassi, spese e residui',
    summary:
      'Riunisce i dati economici operativi di Scadenze e Spese, sempre separati per valuta. Non sostituisce un conto economico fiscale.',
    search: 'analytics incassato speso margine residuo scaduto impegni fornitori',
    sections: [
      section('Indicatori', {
        Incassato: 'Pagamenti effettivi registrati sulle entrate del modulo Scadenze.',
        'Da incassare': 'Residuo ancora aperto sulle entrate previste.',
        Speso: 'Totale delle note spese approvate.',
        'Da approvare': 'Spese registrate che attendono una decisione.',
        'Margine di cassa':
          'Incassato meno spese approvate nella stessa valuta; non è utile fiscale né margine contabile.',
        'Impegni fornitori': 'Residuo delle uscite previste nel modulo Scadenze.'
      }),
      section('Regole di lettura', {
        Valuta: 'EUR, USD e altre valute restano in sezioni distinte e non vengono sommate.',
        'Doppio conteggio':
          'Pagamenti fornitori e note spese sono distinti, perché potrebbero descrivere la stessa uscita.',
        Aggiornamento: 'Gli indicatori cambiano quando registri pagamenti, rettifiche o spese.'
      })
    ]
  },
  {
    id: 'expenses',
    title: 'Spese: costi, approvazione e rimborso',
    summary:
      'Registra un costo sostenuto, lo collega al lavoro e ne segue eventuale approvazione e rimborso.',
    search: 'spese categoria approvazione rifiuto rimborso progetto cliente',
    sections: [
      section('Termini', {
        Rimborsabile: 'La spesa deve essere restituita alla persona che l’ha anticipata.',
        Approvazione: 'Verifica interna che autorizza la spesa o il rimborso.',
        Rifiutata: 'Spesa non accettata; il motivo spiega la decisione.',
        Rimborsata: 'Il rimborso è stato effettuato; non equivale alla semplice approvazione.'
      }),
      section('Azioni', {
        'Nuova spesa': 'Registra importo, data, fornitore e contesto.',
        Approva: 'Conferma la validità della spesa.',
        Rifiuta: 'Richiede una motivazione e chiude negativamente la verifica.',
        'Segna rimborsata': 'Registra che la restituzione economica è avvenuta.',
        Modifica: 'Corregge i valori finché i permessi e lo stato lo consentono.'
      }),
      section('Collegamenti', {
        Cliente: 'Indica a quale relazione commerciale attribuire il costo.',
        Progetto: 'Attribuisce il costo a una commessa senza creare una scadenza.',
        'Metodo di pagamento': 'Descrive come è stata pagata, non esegue il pagamento.'
      })
    ]
  },
  {
    id: 'settings',
    title: 'Impostazioni: origine, precedenza e verifica',
    summary:
      'Configura preferenze e integrazioni. Ogni valore mostra origine e ambito; salvare non prova da solo che un servizio esterno funzioni.',
    search: 'impostazioni origine precedenza utente workspace installazione testa ripristina',
    sections: [
      section('Termini', {
        Utente: 'Valore personale che prevale, quando consentito, su workspace e installazione.',
        Workspace: 'Valore condiviso con i membri del workspace.',
        Installazione: 'Valore globale definito dall’amministratore del sistema.',
        Predefinito: 'Valore usato quando nessun ambito ne definisce uno.',
        Segreto: 'Dato sensibile mascherato; non viene mostrato nuovamente in chiaro.',
        'Chiave tecnica': 'Identificatore stabile usato dal software per quella impostazione.'
      }),
      section('Azioni', {
        Salva: 'Memorizza le modifiche nell’ambito indicato.',
        Testa: 'Esegue, quando previsto, una verifica della configurazione o connettività.',
        Ripristina: 'Rimuove l’override corrente e torna al valore dell’ambito inferiore.',
        'Sessioni e dispositivi': 'Mostra gli accessi disponibili e consente di revocarli.',
        'Revoca sessione': 'Disconnette quel dispositivo; la sessione corrente è indicata.'
      }),
      section('Attenzione', {
        JSON: 'Deve rispettare la sintassi JSON prima del salvataggio.',
        'Servizi esterni':
          'SMTP, webhook e altre integrazioni richiedono credenziali e rete valide oltre al salvataggio.'
      })
    ]
  },
  {
    id: 'access',
    title: 'Accessi: utenti, ruoli e permessi',
    summary:
      'Gestisce chi entra nel workspace e quali azioni può eseguire. I ruoli raggruppano permessi applicativi.',
    search: 'accessi invito utenti ruoli permessi workspace amministratore',
    sections: [
      section('Termini', {
        Invito: 'Richiesta nominativa per aggiungere una persona al workspace.',
        Ruolo: 'Gruppo di permessi assegnabile a un membro.',
        Permesso: 'Autorizzazione puntuale a leggere o gestire una risorsa.',
        'Amministratore workspace':
          'Può gestire il workspace secondo i permessi; non è automaticamente super-admin.',
        'Super-admin': 'Amministra l’installazione e i workspace ERP, non solo un workspace.'
      }),
      section('Azioni', {
        'Invita utente': 'Invia o genera l’invito per l’email indicata.',
        'Cerca utente': 'Filtra membri e inviti visibili.',
        'Assegna ruolo': 'Modifica le autorizzazioni effettive del membro.',
        Disattiva: 'Blocca l’accesso senza cancellare la storia dell’utente.'
      }),
      section('Sicurezza', {
        'Minimo privilegio': 'Assegna solo i permessi necessari al lavoro della persona.',
        'Effetto immediato':
          'Le modifiche ai permessi possono richiedere il ricaricamento della sessione.'
      })
    ]
  },
  {
    id: 'workspaces',
    title: 'Workspace ERP: ambienti separati',
    summary:
      'Crea e amministra ambienti ERP distinti. Ogni workspace ha membri, configurazione e record propri.',
    search: 'workspace ERP crea modifica attiva disattiva slug lingua valuta fuso',
    sections: [
      section('Termini', {
        Workspace: 'Confine organizzativo dei dati: non è una cartella delle Note.',
        Slug: 'Nome breve tecnico, univoco e stabile usato negli indirizzi e nelle integrazioni.',
        'Fuso orario': 'Regola come date e orari vengono interpretati e mostrati.',
        Lingua: 'Locale usato per formati e preferenze del workspace.',
        Valuta: 'Valuta predefinita proposta nei moduli economici.'
      }),
      section('Azioni', {
        'Nuovo workspace': 'Crea un ambiente ERP separato con i valori indicati.',
        Modifica: 'Aggiorna configurazione e nome; valuta con attenzione lo slug.',
        Disattiva: 'Impedisce l’uso operativo senza cancellare i dati.',
        'Apri workspace': 'Passa all’ambiente selezionato se ne fai parte.'
      }),
      section('Sicurezza', {
        Visibilità: 'La sezione è riservata ai super-admin.',
        Separazione: 'Utenti e ruoli devono essere assegnati nel workspace corretto.'
      })
    ]
  },
  {
    id: 'notifications',
    title: 'Notifiche: eventi e consegne',
    summary:
      'Raccoglie eventi applicativi destinati all’utente e mostra il loro stato di lettura o consegna.',
    search: 'notifiche lette non lette email webhook consegna',
    sections: [
      section('Termini', {
        'Non letta': 'Notifica non ancora aperta o marcata come letta.',
        Canale: 'Mezzo di consegna, per esempio interfaccia, email o webhook.',
        Consegna: 'Tentativo di inoltro tramite il canale configurato.'
      }),
      section('Azioni', {
        'Segna letta': 'Aggiorna solo lo stato nell’applicazione.',
        'Segna tutte lette': 'Applica lo stato a tutte le notifiche visibili.',
        Aggiorna: 'Ricarica gli eventi più recenti.'
      }),
      section('Limiti', {
        'Canali esterni':
          'Email e webhook dipendono dalla configurazione in Impostazioni e possono fallire separatamente.'
      })
    ]
  },
  {
    id: 'help',
    title: 'Guida: trova subito ciò che ti serve',
    summary:
      'Cerca con parole normali, un termine o un obiettivo. I risultati includono definizioni, procedure, casi d’uso e collegamenti ai moduli.',
    search: 'come usare ricerca guida termini procedure casi uso',
    sections: [
      section('Come cercare', {
        Domanda: 'Scrivi “come creo un progetto?” oppure “cosa significa fatturabile?”.',
        Termine: 'Cerca una parola dell’interfaccia per leggerne la definizione.',
        Obiettivo: 'Descrivi il risultato, per esempio “registrare lavoro per un cliente”.'
      }),
      section('Risultati', {
        Procedura: 'Sequenza di passaggi operativi verificabili.',
        Glossario: 'Definizione di un termine e indicazione di dove viene usato.',
        'Caso d’uso': 'Percorso tra più moduli per ottenere un risultato completo.'
      })
    ]
  }
];

const pathMatchers: ReadonlyArray<[string, string]> = [
  ['/admin/workspaces', 'workspaces'],
  ['/admin/access', 'access'],
  ['/notifications', 'notifications'],
  ['/settings', 'settings'],
  ['/analytics', 'analytics'],
  ['/expenses', 'expenses'],
  ['/billing', 'billing'],
  ['/time', 'time'],
  ['/tasks', 'tasks'],
  ['/notes', 'notes'],
  ['/projects', 'projects'],
  ['/crm', 'crm'],
  ['/clients', 'clients'],
  ['/help', 'help']
];

export function moduleHelpForPath(pathname: string): ModuleHelpTopic {
  const id =
    pathname === '/' ? 'dashboard' : pathMatchers.find(([path]) => pathname.startsWith(path))?.[1];
  return topics.find((topic) => topic.id === id) ?? topics[0]!;
}

export const moduleHelpTopics = topics;

const exactFieldHelp: Readonly<Record<string, string>> = {
  'client-type':
    'Distingue una persona da un’organizzazione e adatta i dati anagrafici disponibili.',
  'client-status': 'Descrive la fase del rapporto: prospect, attivo, inattivo o archiviato.',
  'opp-stage':
    'Posizione corrente nella pipeline commerciale; influenza la lettura della trattativa.',
  'opp-prob': 'Stima percentuale della possibilità di chiusura. Non modifica il valore economico.',
  'task-project': 'Collega l’attività a una commessa. Il timer manterrà anche questo progetto.',
  'time-task':
    'Attribuisce il consuntivo a un’attività; se collegata, deve essere coerente col progetto.',
  'time-billable': 'Indica che il tempo è valorizzabile verso un cliente, ma non crea una fattura.',
  'bill-renewal': 'Stabilisce se e come la voce prosegue alla prossima scadenza.',
  'expense-reimbursable': 'Indica che la somma è stata anticipata e deve essere restituita.',
  'workspace-slug': 'Identificatore tecnico univoco: usa lettere minuscole, numeri e trattini.'
};

const suffixHelp: Readonly<Record<string, string>> = {
  name: 'Nome riconoscibile mostrato in elenchi, ricerche e collegamenti.',
  title: 'Titolo breve che identifica il record nelle viste operative.',
  description: 'Dettagli utili a capire contenuto, obiettivo o contesto del record.',
  notes: 'Informazioni interne di supporto; non vengono trasformate in attività.',
  type: 'Classificazione che determina come il record viene interpretato.',
  status: 'Fase operativa corrente. Cambiarla può modificare azioni e filtri disponibili.',
  priority: 'Urgenza relativa usata per ordinare il lavoro; non cambia automaticamente le date.',
  client: 'Cliente collegato per attribuzione, ricerca e continuità tra i moduli.',
  project: 'Progetto collegato; permette di ricondurre il record alla stessa commessa.',
  amount: 'Valore monetario nella valuta selezionata.',
  value: 'Valore economico stimato nella valuta indicata.',
  budget: 'Importo pianificato o limite di riferimento; non è un saldo contabile.',
  currency: 'Codice della valuta usata per interpretare e formattare gli importi.',
  date: 'Data di riferimento del record secondo il fuso orario del workspace.',
  start: 'Data o ora da cui decorre il record.',
  end: 'Data o ora di conclusione; deve essere successiva all’inizio.',
  due: 'Scadenza entro cui il lavoro o il traguardo dovrebbe essere completato.',
  next: 'Prossima data prevista per la scadenza o ricorrenza.',
  email: 'Indirizzo usato per contatto, inviti o identificazione, secondo il modulo.',
  phone: 'Numero di contatto; includi il prefisso internazionale quando necessario.',
  website: 'Indirizzo completo del sito, preferibilmente con protocollo https.',
  repo: 'Indirizzo del repository associato al progetto.',
  tags: 'Etichette libere, separate come indicato, per classificare e cercare.',
  labels: 'Etichette operative che facilitano filtri e raggruppamenti.',
  category: 'Categoria usata per raggruppare e analizzare record simili.',
  method: 'Metodo con cui è avvenuta l’operazione; non esegue alcun pagamento.',
  supplier: 'Soggetto da cui è stato acquistato il bene o servizio.',
  party: 'Cliente o fornitore interessato dalla scadenza.',
  frequency: 'Intervallo con cui una voce ricorrente viene riproposta.',
  grace: 'Numero di giorni di margine dopo la scadenza prevista.',
  source: 'Canale o origine da cui è arrivata l’opportunità.',
  action: 'Prossimo passo concreto, con linguaggio operativo e verificabile.',
  loss: 'Motivo per cui la trattativa è stata chiusa senza esito positivo.',
  reason: 'Motivazione registrata per rendere comprensibile e verificabile la decisione.',
  rate: 'Valore economico per ora, applicato solo ai calcoli previsti.',
  contact: 'Persona principale a cui rivolgersi per questo cliente.',
  tax: 'Partita IVA o codice fiscale usato per identificazione amministrativa.',
  acquired: 'Data in cui il prospect è diventato cliente o è stato acquisito.',
  folder: 'Cartella delle Note in cui organizzare il documento; non è un workspace ERP.',
  role: 'Funzione della persona nel progetto o insieme di permessi, secondo il contesto.',
  user: 'Persona da aggiungere o collegare al record.',
  locale: 'Lingua e convenzioni di formattazione predefinite.',
  timezone: 'Fuso orario usato per interpretare date, orari e scadenze.',
  slug: 'Identificatore tecnico breve e univoco, normalmente non destinato alla lettura.',
  reimbursable: 'Indica se la spesa deve essere restituita alla persona che l’ha anticipata.',
  billable: 'Indica se il tempo può essere valorizzato verso il cliente.',
  estimate: 'Durata prevista del lavoro, distinta dal tempo realmente registrato.'
};

export interface FieldHelpContent {
  title: string;
  description: string;
  search: string;
}

const commonTermHelp: Readonly<Record<string, string>> = {
  azioni:
    'Raccoglie i comandi disponibili sul record. Le azioni mostrate dipendono dai tuoi permessi e dallo stato corrente.',
  stato:
    'Indica la fase operativa corrente del record e determina quali passaggi successivi sono disponibili.',
  priorita:
    'Indica l’urgenza relativa. Serve per ordinare il lavoro, ma non cambia automaticamente scadenze o assegnazioni.',
  responsabile:
    'Persona incaricata di seguire il record. Non coincide necessariamente con chi lo ha creato.',
  scadenza:
    'Data entro cui completare il lavoro o gestire il movimento. Viene interpretata nel fuso orario del workspace.',
  budget:
    'Importo pianificato o limite di riferimento. Non rappresenta automaticamente il saldo contabile.',
  cliente:
    'Anagrafica collegata che mantiene la relazione tra CRM, progetti, tempo, spese e scadenze.',
  progetto:
    'Commessa o iniziativa che raggruppa attività, persone, tempo, milestone e valori economici collegati.',
  attivita:
    'Unità concreta di lavoro, facoltativamente collegata a un progetto e utilizzabile per avviare il timer.',
  importo: 'Valore monetario interpretato nella valuta indicata.',
  valuta:
    'Codice monetario usato per formattare e confrontare gli importi. Valute diverse non vengono sommate automaticamente.',
  data: 'Data di riferimento del record secondo il fuso orario del workspace.',
  descrizione:
    'Testo che spiega contenuto, obiettivo o contesto. Non produce automaticamente attività o notifiche.',
  categoria: 'Classificazione usata per filtrare e analizzare record simili.',
  fornitore: 'Soggetto che ha fornito il bene o servizio associato alla spesa.',
  probabilita:
    'Stima percentuale della possibilità di chiusura dell’opportunità; non è una garanzia.',
  fase: 'Passaggio della pipeline commerciale in cui si trova l’opportunità.',
  origine: 'Canale o contesto da cui è arrivata l’opportunità.',
  email:
    'Indirizzo usato per contatto, invito o identificazione. Negli inviti deve coincidere con l’account OIDC.',
  ruolo: 'Insieme di permessi o funzione della persona nel contesto corrente.',
  permessi: 'Autorizzazioni puntuali che stabiliscono quali dati e azioni una persona può usare.',
  workspace:
    'Ambiente ERP separato con membri, configurazione e record propri. Non è una cartella delle Note.',
  modulo: 'Area funzionale del gestionale, attivabile e soggetta ai relativi permessi.',
  origineimpostazione:
    'Ambito da cui proviene il valore effettivo: utente, workspace, installazione o predefinito.',
  kanban:
    'Vista a colonne: ogni colonna rappresenta uno stato o una fase e le schede possono essere spostate quando consentito.',
  elenco: 'Vista tabellare o lineare, utile per leggere molti record, ordinare e applicare filtri.',
  pipeline:
    'Sequenza delle fasi commerciali attraversate da un’opportunità dalla registrazione alla chiusura.',
  membri: 'Persone che partecipano al progetto o al workspace con ruoli e permessi specifici.',
  milestone: 'Traguardo importante del progetto con una data; non è un’attività eseguibile.',
  cartella:
    'Contenitore organizzativo delle Note. Non crea un nuovo workspace ERP e non modifica i permessi.',
  fatturabile:
    'Indica che il tempo può essere valorizzato verso un cliente; non emette una fattura.',
  rimborsabile:
    'Indica che una spesa anticipata deve essere restituita alla persona che l’ha sostenuta.'
};

export function termHelpFor(term: string, pathname: string): FieldHelpContent {
  const topic = moduleHelpForPath(pathname);
  const normalized = normalizeTerm(term);
  const contextualItem = topic.sections
    .flatMap((helpSection) => helpSection.items)
    .find((item) => {
      const candidate = normalizeTerm(item.label);
      return (
        candidate === normalized ||
        (normalized.length >= 4 && candidate.includes(normalized)) ||
        (candidate.length >= 4 && normalized.includes(candidate))
      );
    });
  const description =
    contextualItem?.description ??
    commonTermHelp[normalized] ??
    (normalized === normalizeTerm(topic.id) || normalized.includes(normalizeTerm(topic.id))
      ? topic.summary
      : `“${term}” è un elemento del modulo ${topic.title.split(':')[0]}. Apri la Guida per definizione, procedura e collegamenti con gli altri moduli.`);
  return {
    title: `${term}: significato e utilizzo`,
    description,
    search: `${topic.id} ${term}`
  };
}

function normalizeTerm(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLocaleLowerCase('it')
    .replace(/[^a-z0-9]+/g, '');
}

export function fieldHelpFor(fieldId: string, label: string, pathname: string): FieldHelpContent {
  const topic = moduleHelpForPath(pathname);
  const suffix = [...Object.keys(suffixHelp)]
    .sort((a, b) => b.length - a.length)
    .find((candidate) => fieldId === candidate || fieldId.endsWith(`-${candidate}`));
  const description =
    exactFieldHelp[fieldId] ??
    (suffix ? suffixHelp[suffix] : undefined) ??
    `Questo campo salva “${label}” nel record corrente. Non modifica automaticamente altri moduli salvo collegamenti indicati esplicitamente.`;
  return {
    title: `${label}: cosa significa`,
    description,
    search: `${topic.id} ${label} ${fieldId.replaceAll('-', ' ')}`
  };
}
